import { useCallback, useEffect, useRef } from 'react';
import { isIOS } from '../../../../helpers/browserHelpers';
import { setSSQueryKey } from '../../productNavigationHelper';

/*
 * minimal info required for replacing current browser history entry reflecting:
 * - the absolute page number hosting the clicked product
 * - product's code, so that when the user navigates back to the lister,
 *   we can identify previously clicked product and scroll to the containing row
 */
interface ClickedProductInfo {
  index: number;
  code: string;
}

interface PaginationData {
  pageOffset: number;
}

const beforeUnloadEvent = isIOS() ? 'pagehide' : 'beforeunload';

function useSyncUrlWithClickedProductPage({ pageOffset }: PaginationData): void {
  const callback = useCallback(() => {
    const clickedProductInfo = getClickedProductInfo();
    if (clickedProductInfo) {
      const { pageSize } = window.globalLister.listerData.pagination;
      const actualHostingPage = Math.floor(clickedProductInfo.index / pageSize) + pageOffset;
      updatePageURLParam(actualHostingPage, { code: clickedProductInfo.code });
      setSSQueryKey();
    }
  }, [pageOffset]);
  useEffect(() => {
    window.addEventListener(beforeUnloadEvent, callback);
    return (): void => {
      window.removeEventListener(beforeUnloadEvent, callback);
    };
  }, [callback]);
}

function writeClickedProductInfo(info: ClickedProductInfo): void {
  window.globalLister.clickedProduct = info;
}

function getClickedProductInfo(): ClickedProductInfo | undefined {
  return window.globalLister.clickedProduct;
}

function updatePageURLParam(pageNumber: number, state?: { code: string }): void {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set('page', `${pageNumber}`);
  window.history.replaceState(state ?? null, '', `?${searchParams}`);
}

function useFocusOnLastVisitedProduct(productCode: string): React.RefObject<HTMLDivElement> {
  const productRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const visitedProductCode = window.history.state?.code;
    if (visitedProductCode && visitedProductCode === productCode) {
      productRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'center'
      });
    }
  }, []);

  return productRef;
}
export { writeClickedProductInfo, useSyncUrlWithClickedProductPage, updatePageURLParam, useFocusOnLastVisitedProduct };
